import {
  HotelParams,
  ScenarioKey,
  SCENARIO_FACTORS,
} from '@/models/HotelParams';

/**
 * Risultato completo di un calcolo. Tutti i valori in Euro (salvo `paybackMonths`).
 */
export interface CalcResult {
  actual: Result;
  pms: Result;
  savings: Delta;
  total: Total;
}

export interface Result {
  revenue: number
  otaCommissionCost: number
  employeesHours: number
  employeesCost: number
  systemCost: number
  totalCost: number
}

export interface Delta {
  savingHours: number;
  savingsHoursCost: number;
  commissions: number;
  deltaRevenue: number;
  unnecessaryEmployee: number;
  upsellingIncrease: number;
}

export interface Total {
  netSavings: number;
  paybackMonths: number;
}


export class Calculator {
  params: HotelParams;

  constructor(params: HotelParams) {
    this.params = { ...params };
  }

  /**
   * Calcola i risultati applicando i fattori di scenario
   * (conservativo / realistico / aggressivo) ai benefici attesi.
   * Il costo del sistema resta invariato.
   */
  computeScenario(key: ScenarioKey): CalcResult {
    const f = SCENARIO_FACTORS[key];
    const adjusted: HotelParams = {
      ...this.params,
      manualMinutesPerRoom: this.params.manualMinutesPerRoom * f.savings,
      manualMinutesAdministrative: this.params.manualMinutesAdministrative * f.savings,
      deltaOccupancy: this.params.deltaOccupancy * f.deltaO,
      upsellingIncrease: (this.params.upsellingIncrease ?? 0) * f.trevpar,
    };
    return new Calculator(adjusted).compute();
  }


  computeActual(): Result {
    const { actualSystemCost, employeesCount: employeeCount } = this.params;
    const otaCommissionCost = this.actualOtaCommissionCost();
    const employeeCost = this.employeesCost();
    return {
      revenue: this.actualAnnualRevenue(),
      otaCommissionCost,
      employeesHours: this.totalWorkHoursPerEmployee() * employeeCount,
      employeesCost: employeeCost,
      systemCost: actualSystemCost,
      totalCost: otaCommissionCost + employeeCost + actualSystemCost,
    }

  }

  computePMS(): Result {
    const otaCommissionCost = this.pmsOtaCommissionCost();
    const employeeCost = this.unnecessaryEmployeeCount() === 0
      ? this.employeesCost(this.unnecessaryEmployeeCount()) - this.savingsHoursMoney()
      : this.employeesCost(this.unnecessaryEmployeeCount());
    const systemCost = this.systemCost();

    return {
      revenue: this.pmsAnnualRevenue(),
      otaCommissionCost,
      employeesHours: this.totalWorkHoursPerEmployee() * (this.params.employeesCount - this.unnecessaryEmployeeCount()),
      employeesCost: employeeCost,
      systemCost,
      totalCost: otaCommissionCost + employeeCost + systemCost,
    }

  }

  compute(): CalcResult {
    const { actualSystemCost } = this.params;

    const pms = this.computePMS();
    const actual = this.computeActual();
    const { otaPercentage, otaCommission } = this.params;
    const otaCommissions = otaPercentage * otaCommission * this.pmsAnnualRevenue();

    const savingHoursCost = this.savingsHoursMoney();
    const delta: Delta = {
      savingHours: this.savingsHours(),
      savingsHoursCost: savingHoursCost,
      commissions: otaCommissions - pms.otaCommissionCost,
      deltaRevenue: pms.revenue - actual.revenue,
      unnecessaryEmployee: this.unnecessaryEmployeeCount(),
      upsellingIncrease: this.upsellingIncreaseAmount()
    }

    const netSavings = (pms.revenue - actual.revenue)
      + savingHoursCost
      + (actual.otaCommissionCost - pms.otaCommissionCost)
      + delta.upsellingIncrease
      + actualSystemCost
      - this.systemCost();


    return {
      pms,
      actual,
      savings: delta,
      total: {
        netSavings: netSavings,
        paybackMonths: netSavings <= 0
          ? Infinity
          : (this.systemCost() / netSavings) * 12,
      }
    }
  }

  employeeCost() {
    const { hourlyCost } = this.params;
    return hourlyCost * ((2080 / 365 * this.params.daysPerYear));
  }

  // utils 
  totalWorkHoursPerEmployee() {
    const { daysPerYear } = this.params;

    // risultato teorico ocsa
    // 52 settimane * 40 ore = 2080 ore/anno
    // 2080 ore - ferie (4 settimane) = 1840 ore/anno
    const hoursPerYear = 1730;

    const reductHoursByDaysOff = hoursPerYear / 365 * daysPerYear;
    // const administrativeReduction = this.params.manualMinutesAdministrative / 60 * daysPerYear;

    return reductHoursByDaysOff;
  }

  // Actual
  actualAnnualRevenue(): number {
    const { rooms, occupancy, adr, daysPerYear } = this.params;
    return rooms * occupancy * adr * daysPerYear;
  }

  actualOtaCommissionCost(): number {
    const { otaPercentage, otaCommission } = this.params;
    return otaPercentage * otaCommission * this.actualAnnualRevenue();
  }

  employeesCost(unnecessaryEmployees: number = 0): number {
    const { employeesCount: employeeCount } = this.params;
    return (employeeCount - unnecessaryEmployees) * this.employeeCost();
  }

  // Delta

  /** Risparmio annuo sulle ore di lavoro manuale (h/anno). */
  savingsHours(): number {
    const { rooms, occupancy, manualMinutesPerRoom, daysPerYear, deltaOccupancy } = this.params;
    return (rooms * (occupancy + deltaOccupancy) * (manualMinutesPerRoom / 60) * daysPerYear)
      + (this.params.manualMinutesAdministrative / 60 * daysPerYear)
  }

  /** Numero di dipendenti non necessari. */
  unnecessaryEmployeeCount(): number {

    const { employeesCount } = this.params;
    const savingHours = this.savingsHours();
    const unnecessaryEmployees = Math.floor(savingHours / this.totalWorkHoursPerEmployee());

    return unnecessaryEmployees > employeesCount
      ? employeesCount
      : unnecessaryEmployees;
  }

  /** Valore del risparmio sulle ore di lavoro manuale (€/anno). */
  savingsHoursMoney(): number {

    return this.savingsHours() * this.params.hourlyCost;
  }

  // PMS
  pmsAnnualRevenue(): number {
    const { rooms, occupancy, adr, daysPerYear, deltaOccupancy, upsellingIncrease } = this.params;
    return rooms * (occupancy + deltaOccupancy) * adr * (1 + upsellingIncrease) * daysPerYear;
  }

  pmsOtaCommissionCost(): number {
    const { otaPercentage, otaCommission, directOccupancyIncrease } = this.params;
    return (otaPercentage - directOccupancyIncrease) * otaCommission * this.pmsAnnualRevenue();
  }


  /** Valore dell'incremento di TRevPAR (€/anno). 0 se non specificato. */
  upsellingIncreaseAmount(): number {
    const { upsellingIncrease, rooms, occupancy, deltaOccupancy, adr, daysPerYear } = this.params;
    if (!upsellingIncrease) return 0;
    return upsellingIncrease * rooms * (occupancy + deltaOccupancy) * adr * daysPerYear;
  }

  private PMSCommissionRate(): number {
    const month = 12;
    const otaChannels = 2;
    const otaChannelsCost = 10;
    const pmsRate = 0.005;

    const channelManagerCost = otaChannels * otaChannelsCost * month;
    const { adr, rooms, daysPerYear, deltaOccupancy, occupancy } = this.params;
    const net = adr * rooms * daysPerYear * (occupancy + deltaOccupancy);
    return net * pmsRate + channelManagerCost;
  }

  /** Costo annuo del sistema (€/anno). */
  systemCost(): number {
    const { systemAnnualCost } = this.params;
    return systemAnnualCost + Math.min(this.PMSCommissionRate(), 10_000);
  }


  /**
   * Payback period in mesi.
   * Se il risparmio netto è ≤ 0 restituisce `Infinity` (mai rientro).
   */
  paybackMonths(netSavings: number): number {
    if (netSavings <= 0) return Infinity;
    return (this.systemCost() / netSavings) * 12;
  }
}
